import {
  Sport,
  Country,
  League,
  Club,
  Season,
  Competition,
  Matchday,
  UserDashboardData,
  UpdateSportPreferenceRequest,
  UpdateClubPreferenceRequest,
  PaginatedLeaguesResponse
} from '../types/sports';
import { getToken } from '../utils/auth';
import { getPartnerToken } from '../utils/partnerAuth';

// Resolve API base: use absolute VITE_API_BASE when provided; otherwise current origin.
const _configured = (import.meta as any).env?.VITE_API_BASE as string | undefined;
const API_BASE_URL = _configured && /^https?:\/\//i.test(_configured) ? _configured : window.location.origin;


// Helper function to make authenticated requests
const makeAuthenticatedRequest = async (url: string, options: RequestInit = {}) => {
  // Try to get club token first, then partner token
  let token = getToken();
  let tokenType = 'club';

  if (!token) {
    token = getPartnerToken();
    tokenType = 'partner';
  }

  if (!token) {
    console.error('No token found for authenticated request to:', url);
  } else {
    console.log(`Making authenticated request to: ${url} with ${tokenType} token`);
  }

  const headers = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers,
  };

  const response = await fetch(`${API_BASE_URL}/api${url}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    console.error(`API request failed: ${url} - Status: ${response.status}`);
    if (response.status === 401) {
      console.error('Unauthorized! Token may be invalid or expired');
    }
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  // Check if response has content before parsing JSON
  const contentType = response.headers.get('content-type');
  const contentLength = response.headers.get('content-length');

  // If no content or content-length is 0, return undefined
  if (contentLength === '0' || !contentType?.includes('application/json')) {
    return undefined;
  }

  // Try to parse JSON, but handle empty responses
  const text = await response.text();
  if (!text || text.trim() === '') {
    return undefined;
  }

  return JSON.parse(text);
};

export const sportsApi = {
  // Sports
  getAllSports: async (): Promise<Sport[]> => {
    return makeAuthenticatedRequest('/sports');
  },

  getSportById: async (id: number): Promise<Sport> => {
    return makeAuthenticatedRequest(`/sports/${id}`);
  },

  // Countries
  getAllCountries: async (): Promise<Country[]> => {
    return makeAuthenticatedRequest('/sports/countries');
  },

  getCountryById: async (id: number): Promise<Country> => {
    return makeAuthenticatedRequest(`/sports/countries/${id}`);
  },

  createCountry: async (data: {
    name: string;
    code: string;
    alpha2Code?: string;
    flag?: string;
  }): Promise<Country> => {
    return makeAuthenticatedRequest('/sports/countries', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  updateCountry: async (id: number, data: {
    name: string;
    code: string;
    alpha2Code?: string;
    flag?: string;
    isActive?: boolean;
  }): Promise<Country> => {
    return makeAuthenticatedRequest(`/sports/countries/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  canDeleteCountry: async (id: number): Promise<{ canDelete: boolean }> => {
    return makeAuthenticatedRequest(`/sports/countries/${id}/can-delete`);
  },

  deleteCountry: async (id: number): Promise<void> => {
    return makeAuthenticatedRequest(`/sports/countries/${id}`, {
      method: 'DELETE',
    });
  },

  // Sports CRUD operations
  createSport: async (data: {
    name: string;
    description?: string;
    icon?: string;
  }): Promise<Sport> => {
    return makeAuthenticatedRequest('/sports', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  updateSport: async (id: number, data: {
    name: string;
    description?: string;
    icon?: string;
    isActive?: boolean;
  }): Promise<Sport> => {
    return makeAuthenticatedRequest(`/sports/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  canDeleteSport: async (id: number): Promise<{ canDelete: boolean }> => {
    return makeAuthenticatedRequest(`/sports/${id}/can-delete`);
  },

  deleteSport: async (id: number): Promise<void> => {
    return makeAuthenticatedRequest(`/sports/${id}`, {
      method: 'DELETE',
    });
  },

  // Leagues CRUD
  createLeague: async (data: {
    name: string;
    shortName?: string;
    description?: string;
    level?: number;
    sportId: number;
    countryId: number;
  }): Promise<League> => {
    return makeAuthenticatedRequest('/sports/leagues', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  updateLeague: async (id: number, data: {
    name: string;
    shortName?: string;
    description?: string;
    level?: number;
    isActive?: boolean;
    sportId?: number;
    countryId?: number;
  }): Promise<League> => {
    return makeAuthenticatedRequest(`/sports/leagues/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  canDeleteLeague: async (id: number): Promise<{ canDelete: boolean }> => {
    return makeAuthenticatedRequest(`/sports/leagues/${id}/can-delete`);
  },

  deleteLeague: async (id: number): Promise<void> => {
    return makeAuthenticatedRequest(`/sports/leagues/${id}`, {
      method: 'DELETE',
    });
  },

  // Clubs CRUD
  createClub: async (data: {
    name: string;
    shortName?: string;
    description?: string;
    city?: string;
    stadium?: string;
    stadiumAddress?: string;
    sportId: number;
    countryId: number;
    leagueId?: number;
  }): Promise<Club> => {
    return makeAuthenticatedRequest('/sports/clubs', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  updateClub: async (id: number, data: {
    name: string;
    shortName?: string;
    description?: string;
    city?: string;
    stadium?: string;
    stadiumAddress?: string;
    isActive?: boolean;
    sportId?: number;
    countryId?: number;
    leagueId?: number;
  }): Promise<Club> => {
    return makeAuthenticatedRequest(`/sports/clubs/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  canDeleteClub: async (id: number): Promise<{ canDelete: boolean }> => {
    return makeAuthenticatedRequest(`/sports/clubs/${id}/can-delete`);
  },

  deleteClub: async (id: number): Promise<void> => {
    return makeAuthenticatedRequest(`/sports/clubs/${id}`, {
      method: 'DELETE',
    });
  },

  // Leagues
  getAllLeagues: async (sportId?: number, countryId?: number): Promise<League[]> => {
    const params = new URLSearchParams();
    if (sportId) params.append('sportId', sportId.toString());
    if (countryId) params.append('countryId', countryId.toString());

    const queryString = params.toString();
    const url = `/sports/leagues${queryString ? `?${queryString}` : ''}`;

    const response = await makeAuthenticatedRequest(url);
    return response.leagues || [];
  },

  getLeaguesPaginated: async (
    sportId?: number,
    countryId?: number,
    page: number = 1,
    pageSize: number = 50,
    includeInactive: boolean = false
  ): Promise<PaginatedLeaguesResponse> => {
    const params = new URLSearchParams();
    if (sportId) params.append('sportId', sportId.toString());
    if (countryId) params.append('countryId', countryId.toString());
    params.append('page', page.toString());
    params.append('pageSize', pageSize.toString());
    params.append('includeInactive', includeInactive.toString());

    const url = `/sports/leagues?${params.toString()}`;

    return makeAuthenticatedRequest(url);
  },

  getLeagueById: async (id: number): Promise<League> => {
    return makeAuthenticatedRequest(`/sports/leagues/${id}`);
  },

  getLeaguesBySportAndCountry: async (sportId: number, countryId: number): Promise<League[]> => {
    return makeAuthenticatedRequest(`/sports/leagues?sportId=${sportId}&countryId=${countryId}`);
  },

  // Clubs
  getAllClubs: async (sportId?: number, countryId?: number, leagueId?: number): Promise<Club[]> => {
    const params = new URLSearchParams();
    if (sportId) params.append('sportId', sportId.toString());
    if (countryId) params.append('countryId', countryId.toString());
    if (leagueId) params.append('leagueId', leagueId.toString());

    const queryString = params.toString();
    const url = `/sports/clubs${queryString ? `?${queryString}` : ''}`;

    const result = await makeAuthenticatedRequest(url);
    return result ?? [];
  },

  getClubById: async (id: number): Promise<Club> => {
    return makeAuthenticatedRequest(`/sports/clubs/${id}`);
  },

  // Seasons
  getAllSeasons: async (): Promise<Season[]> => {
    return makeAuthenticatedRequest('/sports/seasons');
  },

  getSeasonsByLeague: async (leagueId: number): Promise<Season[]> => {
    return makeAuthenticatedRequest(`/sports/leagues/${leagueId}/seasons`);
  },

  getCurrentSeasonByLeague: async (leagueId: number): Promise<Season> => {
    return makeAuthenticatedRequest(`/sports/leagues/${leagueId}/seasons/current`);
  },

  getSeasonById: async (id: number): Promise<Season> => {
    return makeAuthenticatedRequest(`/sports/seasons/${id}`);
  },

  createSeason: async (data: {
    name: string;
    startDate: string;
    endDate: string;
    leagueId: number;
    isActive?: boolean;
    isCurrent?: boolean;
  }): Promise<Season> => {
    return makeAuthenticatedRequest('/sports/seasons', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  updateSeason: async (id: number, data: {
    name?: string;
    startDate?: string;
    endDate?: string;
    leagueId?: number;
    isActive?: boolean;
    isCurrent?: boolean;
  }): Promise<Season> => {
    return makeAuthenticatedRequest(`/sports/seasons/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  canDeleteSeason: async (id: number): Promise<{ canDelete: boolean }> => {
    return makeAuthenticatedRequest(`/sports/seasons/${id}/canDelete`);
  },

  deleteSeason: async (id: number): Promise<void> => {
    return makeAuthenticatedRequest(`/sports/seasons/${id}`, {
      method: 'DELETE',
    });
  },

  toggleSeasonActive: async (id: number): Promise<Season> => {
    return makeAuthenticatedRequest(`/sports/seasons/${id}/toggle-active`, {
      method: 'PATCH',
    });
  },

  // Competitions removed - table doesn't exist

  // Matchdays
  getAllMatchdays: async (): Promise<Matchday[]> => {
    return makeAuthenticatedRequest('/sports/matchdays');
  },

  getMatchdaysBySeasonId: async (seasonId: number): Promise<Matchday[]> => {
    return makeAuthenticatedRequest(`/sports/matchdays/season/${seasonId}`);
  },

  getMatchdaysByCompetitionId: async (competitionId: number): Promise<Matchday[]> => {
    return makeAuthenticatedRequest(`/sports/matchdays/competition/${competitionId}`);
  },

  getMatchdayById: async (id: number): Promise<Matchday> => {
    return makeAuthenticatedRequest(`/sports/matchdays/${id}`);
  },

  createMatchday: async (data: {
    name: string;
    number: number;
    startDate?: string;
    endDate?: string;
    isCompleted?: boolean;
    isActive?: boolean;
    seasonId: number;
    competitionId?: number;
  }): Promise<Matchday> => {
    return makeAuthenticatedRequest('/sports/matchdays', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  updateMatchday: async (id: number, data: {
    name?: string;
    number?: number;
    startDate?: string;
    endDate?: string;
    isCompleted?: boolean;
    isActive?: boolean;
    seasonId?: number;
    competitionId?: number;
  }): Promise<Matchday> => {
    return makeAuthenticatedRequest(`/sports/matchdays/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  canDeleteMatchday: async (id: number): Promise<{ canDelete: boolean }> => {
    return makeAuthenticatedRequest(`/sports/matchdays/${id}/canDelete`);
  },

  deleteMatchday: async (id: number): Promise<void> => {
    return makeAuthenticatedRequest(`/sports/matchdays/${id}`, {
      method: 'DELETE',
    });
  },

  toggleMatchdayActive: async (id: number): Promise<Matchday> => {
    return makeAuthenticatedRequest(`/sports/matchdays/${id}/toggle-active`, {
      method: 'PATCH',
    });
  },

  toggleMatchdayCompleted: async (id: number): Promise<Matchday> => {
    return makeAuthenticatedRequest(`/sports/matchdays/${id}/toggle-completed`, {
      method: 'PATCH',
    });
  },

  // Matches
  getAllMatches: async (): Promise<any[]> => {
    return makeAuthenticatedRequest('/sports/matches');
  },

  createMatch: async (matchData: {
    date: string;
    scheduledDate: string;
    actualDate?: string;
    homeScore?: number;
    awayScore?: number;
    status: number;
    notes?: string;
    matchdayId: number;
    homeClubId?: number;
    awayClubId?: number;
  }): Promise<any> => {
    return makeAuthenticatedRequest('/sports/matches', {
      method: 'POST',
      body: JSON.stringify(matchData),
    });
  },

  updateMatch: async (id: number, matchData: {
    date?: string;
    scheduledDate?: string;
    actualDate?: string;
    homeScore?: number;
    awayScore?: number;
    status?: number;
    notes?: string;
    matchdayId?: number;
    homeClubId?: number;
    awayClubId?: number;
  }): Promise<any> => {
    return makeAuthenticatedRequest(`/sports/matches/${id}`, {
      method: 'PUT',
      body: JSON.stringify(matchData),
    });
  },

  deleteMatch: async (id: number): Promise<void> => {
    return makeAuthenticatedRequest(`/sports/matches/${id}`, {
      method: 'DELETE',
    });
  },

  // Create an own match (Testspiel/Training). Ensures a "Saisonvorbereitung"
  // competition and a Testspieltag matchday for the season. Endpoint lives on
  // the MatchesController (/api/matches/own), not the sports route.
  createOwnMatch: async (request: {
    seasonId: number;
    matchType: number; // 1 = Friendly (Testspiel), 2 = Training
    scheduledDate: string;
    isHome?: boolean;
    ownClubId?: number;
    ownTeamName?: string;
    opponentClubId?: number;
    opponentTeamName?: string;
    location?: string;
    locationAddress?: string;
    notes?: string;
    matchdayId?: number;
    matchdayName?: string;
  }): Promise<any> => {
    const response = await makeAuthenticatedRequest('/matches/own', {
      method: 'POST',
      body: JSON.stringify(request),
    });
    return response?.data ?? response;
  },

  // Convenience methods for the new pages
  getSports: async () => {
    return sportsApi.getAllSports();
  },

  getCountries: async () => {
    return sportsApi.getAllCountries();
  },

  getLeagues: async () => {
    return sportsApi.getAllLeagues();
  },

  getClubs: async () => {
    return sportsApi.getAllClubs();
  },

  getSeasons: async () => {
    return sportsApi.getAllSeasons();
  },

  // getCompetitions removed - table doesn't exist

  getMatchdays: async () => {
    return sportsApi.getAllMatchdays();
  },

  getMatches: async () => {
    return sportsApi.getAllMatches();
  },

  // Get upcoming matches within a date range
  getUpcomingMatches: async (daysAhead: number = 30): Promise<any[]> => {
    const today = new Date();
    const futureDate = new Date();
    futureDate.setDate(today.getDate() + daysAhead);

    const fromDate = today.toISOString().split('T')[0];
    const toDate = futureDate.toISOString().split('T')[0];

    return makeAuthenticatedRequest(`/sports/matches?fromDate=${fromDate}&toDate=${toDate}`);
  },

  // Get matches by club for upcoming games
  getUpcomingMatchesByClub: async (clubId: number, daysAhead: number = 30): Promise<any[]> => {
    const today = new Date();
    const futureDate = new Date();
    futureDate.setDate(today.getDate() + daysAhead);

    const fromDate = today.toISOString().split('T')[0];
    const toDate = futureDate.toISOString().split('T')[0];

    return makeAuthenticatedRequest(`/sports/matches?clubId=${clubId}&fromDate=${fromDate}&toDate=${toDate}`);
  },

  // Get all past matches (no date limit)
  getPastMatches: async (): Promise<any[]> => {
    const today = new Date();
    const toDate = today.toISOString().split('T')[0];

    // Load all past matches without a fromDate limit
    return makeAuthenticatedRequest(`/sports/matches?toDate=${toDate}`);
  },

  // Get all past matches by club (no date limit)
  getPastMatchesByClub: async (clubId: number): Promise<any[]> => {
    const today = new Date();
    const toDate = today.toISOString().split('T')[0];

    // Load all past matches for the club without a fromDate limit
    return makeAuthenticatedRequest(`/sports/matches?clubId=${clubId}&toDate=${toDate}`);
  },

  // Competitions
  getAllCompetitions: async (): Promise<Competition[]> => {
    return makeAuthenticatedRequest('/sports/competitions');
  },

  getCompetitionsBySeasonId: async (seasonId: number): Promise<Competition[]> => {
    return makeAuthenticatedRequest(`/sports/competitions/season/${seasonId}`);
  },

  getCompetitionById: async (id: number): Promise<Competition> => {
    return makeAuthenticatedRequest(`/sports/competitions/${id}`);
  },

  createCompetition: async (data: {
    name: string;
    shortName?: string;
    description?: string;
    startDate?: string;
    endDate?: string;
    seasonId: number;
    isActive?: boolean;
  }): Promise<Competition> => {
    return makeAuthenticatedRequest('/sports/competitions', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  updateCompetition: async (id: number, data: {
    name?: string;
    shortName?: string;
    description?: string;
    startDate?: string;
    endDate?: string;
    seasonId?: number;
    isActive?: boolean;
  }): Promise<Competition> => {
    return makeAuthenticatedRequest(`/sports/competitions/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  canDeleteCompetition: async (id: number): Promise<{ canDelete: boolean }> => {
    return makeAuthenticatedRequest(`/sports/competitions/${id}/canDelete`);
  },

  deleteCompetition: async (id: number): Promise<void> => {
    return makeAuthenticatedRequest(`/sports/competitions/${id}`, {
      method: 'DELETE',
    });
  },

  toggleCompetitionActive: async (id: number): Promise<Competition> => {
    return makeAuthenticatedRequest(`/sports/competitions/${id}/toggle-active`, {
      method: 'PATCH',
    });
  },

  getCompetitions: async () => {
    return sportsApi.getAllCompetitions();
  },

  // User preferences
  updateSportPreference: async (request: UpdateSportPreferenceRequest): Promise<{ message: string }> => {
    return makeAuthenticatedRequest('/sports/preferences/sport', {
      method: 'PUT',
      body: JSON.stringify(request),
    });
  },

  updateClubPreference: async (request: UpdateClubPreferenceRequest): Promise<{ message: string }> => {
    return makeAuthenticatedRequest('/sports/preferences/club', {
      method: 'PUT',
      body: JSON.stringify(request),
    });
  },

  updateMatchColorPreferences: async (homeMatchColor?: string, awayMatchColor?: string): Promise<{ message: string }> => {
    return makeAuthenticatedRequest('/sports/preferences/colors', {
      method: 'PUT',
      body: JSON.stringify({ homeMatchColor, awayMatchColor }),
    });
  },

  updateViewModePreference: async (viewMode?: string): Promise<{ message: string }> => {
    return makeAuthenticatedRequest('/sports/preferences/viewmode', {
      method: 'PUT',
      body: JSON.stringify({ viewMode }),
    });
  },

  updateDrivingTimesPreference: async (showDrivingTimes: boolean): Promise<{ message: string }> => {
    return makeAuthenticatedRequest('/sports/preferences/drivingtimes', {
      method: 'PUT',
      body: JSON.stringify({ showDrivingTimes }),
    });
  },

  // Dashboard
  getUserDashboard: async (): Promise<UserDashboardData> => {
    return makeAuthenticatedRequest('/sports/dashboard');
  },
};
